import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import {
  REQUEST_SETUP_MFA_EMAIL_TOTP,
  CONFIRM_MFA_EMAIL_TOTP,
  RequestSetupMfaEmailTotpResponse,
} from '../graphql/mfa';
import { otpCode8Schema } from '../schemas/mfaValidation';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ButtonComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DialogComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPGroupComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPSlotComponentType = React.ComponentType<any>;

export interface MfaEmailSetupProps {
  userEmail: string;
  onSuccess: () => void;
  onCancel: () => void;
  DialogComponent: DialogComponentType;
  ButtonComponent: ButtonComponentType;
  InputOTPComponent: InputOTPComponentType;
  InputOTPGroupComponent: InputOTPGroupComponentType;
  InputOTPSlotComponent: InputOTPSlotComponentType;
}

export function MfaEmailSetup({
  userEmail,
  onSuccess,
  onCancel,
  DialogComponent,
  ButtonComponent,
  InputOTPComponent,
  InputOTPGroupComponent,
  InputOTPSlotComponent,
}: MfaEmailSetupProps) {
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [provisionRequestId, setProvisionRequestId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const hasSentInitialCode = useRef(false);

  const sendCode = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = (await client.mutate({
        mutation: REQUEST_SETUP_MFA_EMAIL_TOTP,
      })) as { data?: RequestSetupMfaEmailTotpResponse };
      const data = result.data?.requestSetupMfaEmailTotpForMe.mfaEmailTotpProvisionRequest;
      if (data) {
        setProvisionRequestId(data.id);
        setCodeSent(true);
      }
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (hasSentInitialCode.current) return;
    hasSentInitialCode.current = true;
    sendCode();
  }, [sendCode]);

  const handleResend = useCallback(async () => {
    setResending(true);
    setError(null);
    setCode('');
    try {
      const result = (await client.mutate({
        mutation: REQUEST_SETUP_MFA_EMAIL_TOTP,
      })) as { data?: RequestSetupMfaEmailTotpResponse };
      const data = result.data?.requestSetupMfaEmailTotpForMe.mfaEmailTotpProvisionRequest;
      if (data) {
        setProvisionRequestId(data.id);
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  }, [client]);

  const handleConfirm = useCallback(async () => {
    if (!provisionRequestId) return;

    const result = otpCode8Schema.safeParse({ code });
    if (!result.success) {
      setError(result.error.issues[0].message);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await client.mutate({
        mutation: CONFIRM_MFA_EMAIL_TOTP,
        variables: {
          id: provisionRequestId,
          input: { auth_code: code },
        },
      });
      onSuccess();
    } catch (err) {
      setError('Invalid code. Please check and try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  }, [client, provisionRequestId, code, onSuccess]);

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) {
        onCancel();
      }
    },
    [onCancel]
  );

  const maskedEmail = userEmail.replace(/(.{2})(.*)(@.*)/, '$1***$3');

  return (
    <DialogComponent
      isOpen={true}
      setIsOpen={handleClose}
      title="Set up Email Verification"
      centered
    >
      <div className="flex flex-col gap-6 items-center mx-auto w-full max-w-md">
        {error && (
          <div className="w-full p-3 text-sm text-red-800 bg-red-100 dark:bg-red-900/30 dark:text-red-300 rounded-lg">
            {error}
          </div>
        )}

        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full dark:bg-blue-900">
              <span className="text-3xl text-blue-600 material-symbols-outlined dark:text-blue-400">
                mail
              </span>
            </div>
          </div>
          {codeSent ? (
            <p className="text-grey-600 dark:text-grey-300">
              We sent an 8-digit verification code to <strong>{maskedEmail}</strong>. Enter the code
              below to enable email verification.
            </p>
          ) : (
            <p className="text-grey-600 dark:text-grey-300">
              Sending verification code to your email...
            </p>
          )}
        </div>

        {codeSent && (
          <>
            <InputOTPComponent value={code} onChange={setCode} maxLength={8} disabled={loading}>
              <InputOTPGroupComponent>
                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <InputOTPSlotComponent key={i} index={i} />
                ))}
              </InputOTPGroupComponent>
            </InputOTPComponent>

            <div className="flex flex-col gap-3 w-full">
              <ButtonComponent
                type="button"
                onClick={handleConfirm}
                disabled={code.length !== 8 || loading}
                loading={loading}
              >
                Verify and Enable
              </ButtonComponent>
              <ButtonComponent
                type="button"
                variant="secondary"
                onClick={handleResend}
                disabled={resending || loading}
                loading={resending}
              >
                Resend Code
              </ButtonComponent>
              <ButtonComponent
                type="button"
                variant="tertiary"
                onClick={onCancel}
                disabled={loading || resending}
              >
                Cancel
              </ButtonComponent>
            </div>
          </>
        )}

        {!codeSent && loading && (
          <div className="flex flex-col gap-3 w-full">
            <ButtonComponent type="button" variant="tertiary" onClick={onCancel}>
              Cancel
            </ButtonComponent>
          </div>
        )}
      </div>
    </DialogComponent>
  );
}
